/**
 * The design point's hand calculation: one read, one edit, one redraft.
 *
 * An editor's read is `GET …/hand-calc` (ENG-2450) — the first open drafts
 * the record and saves it server-side, and every later open replays what is
 * stored. A viewer's read is `GET …/hand-calc/record`, which never drafts
 * (ENG-2861): a viewer only ever sees what an editor saved. Both land in the
 * one entry. There is no solve route and nothing to confirm, so the browser
 * holds no second copy of the answer: the query IS the record, and both
 * `useHandCalcEdit` and `useHandCalcRegenerate` write the response they get
 * back into that same entry.
 */
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  handleHandCalcCatalogueStudiesStudyIdPointsDpIdHandCalcCatalogueGet,
  handleHandCalcEditStudiesStudyIdPointsDpIdHandCalcPatch,
  handleGetHandCalcRecordStudiesStudyIdPointsDpIdHandCalcRecordGet,
  handleHandCalcGraphStudiesStudyIdPointsDpIdHandCalcGet,
  handleHandCalcRegenerateStudiesStudyIdPointsDpIdHandCalcRegeneratePost,
} from '@/api/generated/sdk.gen';
import type {
  CatalogueOffer,
  EditRequest,
  HandCalcGraphResponse,
} from '@/api/generated/types.gen';
import { apiErrorMessage } from '@/utils/apiErrorMessage';

/**
 * The route refused. `status` is what tells a terminal refusal from a
 * transient one: a 404 (no such point), a 409 (no completed simulation to
 * read, or an attempt that moved under an edit) and a 422 (a case this build
 * cannot read, or an edit the checks rejected) are facts about the request,
 * not about the network, so the caller must not offer a blind retry. A record
 * the advisor could not draft is not among them — an open answers 200 with a
 * `rejection_reason` the panel renders, and a redraft that could not reach
 * the advisor answers 503, the one refusal here worth trying again.
 */
export class HandCalcGraphError extends Error {
  readonly status: number;
  /** The route's own reason, when it gave one (a 422 always does). */
  readonly detail: string | null;
  /**
   * The body carried a list longer than the record holds (ENG-2595). The
   * route states schema errors by type only, never by message, so this is
   * read off that type rather than left as a bare "refused".
   */
  readonly limitReached: boolean;
  constructor(
    status: number,
    detail: string | null = null,
    limitReached = false,
  ) {
    super(`The hand-calc request failed (${status})`);
    this.name = 'HandCalcGraphError';
    this.status = status;
    this.detail = detail;
    this.limitReached = limitReached;
  }
}

const RECORD_LISTS = new Set(['attach', 'reparent', 'values']);

/**
 * Whether a validation body refused one of the record's own lists for its
 * length — not a relation's `bindings` or `inputs`, which are refused with
 * the same type one level down (`body.attach.3.inputs`).
 */
export function tooLong(error: unknown): boolean {
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  return (
    Array.isArray(detail) &&
    detail.some((item) => {
      const { type, loc } = (item ?? {}) as { type?: unknown; loc?: unknown };
      return (
        type === 'too_long' &&
        Array.isArray(loc) &&
        RECORD_LISTS.has(String(loc.at(-1)))
      );
    })
  );
}

/**
 * The edit was saved, but against a run this panel is not showing.
 *
 * It is not a refusal — the write happened — so it carries no status and is
 * deliberately not a `HandCalcGraphError`. What it means for the reader is
 * that the record on screen is now the older one, which is why the mutation
 * rejects with it rather than resolving: a caller that marked the panel Saved
 * on success would say so over a record the cache never took.
 */
export class HandCalcStaleAttemptError extends Error {
  /** The attempt the answer describes, which is not the one on screen. */
  readonly attemptId: string;
  constructor(attemptId: string) {
    super(`The edit answered for attempt ${attemptId}`);
    this.name = 'HandCalcStaleAttemptError';
    this.attemptId = attemptId;
  }
}

/**
 * A read's answer, or the refusal the route made. A transport failure arrives
 * with no response at all; 0 says "not a refusal the route made", which is
 * the retryable case.
 */
function answered<T>(result: {
  data?: T;
  error?: unknown;
  response?: Response;
}): T {
  if (result.data === undefined)
    throw new HandCalcGraphError(
      result.response?.status ?? 0,
      apiErrorMessage(result.error) || null,
    );
  return result.data;
}

/** Reopening the panel cannot change any of these. */
const TERMINAL_HAND_CALC_STATUSES = new Set([404, 409, 422]);

export function isTerminalHandCalcError(error: unknown): boolean {
  return (
    error instanceof HandCalcGraphError &&
    TERMINAL_HAND_CALC_STATUSES.has(error.status)
  );
}

/**
 * The record's cache entry. The shape is `['hand-calc', study, point,
 * attempt]` and the positions are read elsewhere — the report snapshot keeps
 * everything under `'hand-calc'`, and the export's point filter reads
 * `queryKey[2]` — so the four parts stay in this order.
 */
export const handCalcRecordKey = (
  study: string,
  point: string,
  attempt: string,
) => ['hand-calc', study, point, attempt] as const;

/** The offers that apply to this record, rebuilt by the backend on every edit. */
export const handCalcCatalogueKey = (
  study: string,
  point: string,
  attempt: string,
) => ['hand-calc-catalogue', study, point, attempt] as const;

function handCalcRecordOptions(
  studyId: string,
  dpId: string,
  simulationId: string,
  canEdit: boolean,
  enabled = true,
) {
  return queryOptions({
    queryKey: handCalcRecordKey(studyId, dpId, simulationId),
    queryFn: async () => {
      const path = { path: { study_id: studyId, dp_id: dpId } };
      // Only an editor's open drafts. A viewer reads what is saved, and a
      // point no editor has opened answers with `record: null`.
      const data = answered(
        canEdit
          ? await handleHandCalcGraphStudiesStudyIdPointsDpIdHandCalcGet(path)
          : await handleGetHandCalcRecordStudiesStudyIdPointsDpIdHandCalcRecordGet(
              path,
            ),
      );
      // The route captures the attempt before it reads anything else, so a
      // retry that re-links the point mid-request comes back named.
      if (data.graph.attempt_id !== simulationId) {
        throw new Error('The simulation attempt changed');
      }
      return data;
    },
    enabled: enabled && Boolean(studyId && dpId && simulationId),
    // The attempt is part of the identity, so a finished run's record never
    // goes stale on its own; an edit publishes the new one through the
    // mutation rather than through a refetch.
    staleTime: 'static',
    gcTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * The design point's record and its cards.
 *
 * Every surface that shows a hand calculation reads this one entry, so the
 * detail panel's mount is the only request: opening the tab afterwards finds
 * it cached. `canEdit` (`canEditStudy(study)`) picks the read: an editor's
 * drafts a missing record, a viewer's never does.
 */
export function useHandCalcRecord(
  studyId: string,
  dpId: string,
  simulationId: string,
  canEdit: boolean,
  enabled = true,
) {
  const query = useQuery(
    handCalcRecordOptions(studyId, dpId, simulationId, canEdit, enabled),
  );
  return {
    query,
    data: query.data,
    record: query.data?.record ?? null,
    cards: query.data?.cards ?? [],
  };
}

/** The saved record's own entry, read without drafting (ENG-2622). */
export const savedHandCalcRecordKey = (study: string, point: string) =>
  ['hand-calc-saved', study, point] as const;

/**
 * The design point's saved record as it stands, read WITHOUT drafting.
 *
 * `GET …/hand-calc/record` never runs the advisor (ENG-2572), which is what a
 * surface outside the panel needs: an approval card that read through
 * `useHandCalcRecord` would spend a metered draft on a point with no record.
 * Stale on arrival (react-query's default), because the card asks about the
 * record as it is now; its own key, so it never overwrites the panel's entry.
 */
export function useSavedHandCalcRecord(studyId: string, dpId: string) {
  return useQuery(savedHandCalcRecordOptions(studyId, dpId));
}

/** The pure read's own entry; `HandCalcGraphsProvider` reads it on the share page. */
export function savedHandCalcRecordOptions(studyId: string, dpId: string) {
  return queryOptions({
    queryKey: savedHandCalcRecordKey(studyId, dpId),
    queryFn: async () =>
      answered(
        await handleGetHandCalcRecordStudiesStudyIdPointsDpIdHandCalcRecordGet({
          path: { study_id: studyId, dp_id: dpId },
        }),
      ),
    enabled: Boolean(studyId && dpId),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

function handCalcCatalogueOptions(
  studyId: string,
  dpId: string,
  simulationId: string,
  enabled = true,
) {
  return queryOptions({
    queryKey: handCalcCatalogueKey(studyId, dpId, simulationId),
    queryFn: async (): Promise<CatalogueOffer[]> =>
      answered(
        await handleHandCalcCatalogueStudiesStudyIdPointsDpIdHandCalcCatalogueGet(
          { path: { study_id: studyId, dp_id: dpId } },
        ),
      ),
    enabled: enabled && Boolean(studyId && dpId && simulationId),
    // The offers name the record's own groups, so they are only current for
    // the record that was read; `useHandCalcEdit` invalidates them.
    staleTime: 'static',
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** The relations that can still be filed here, for the picker. */
export function useHandCalcCatalogue(
  studyId: string,
  dpId: string,
  simulationId: string,
  enabled = true,
) {
  return useQuery(
    handCalcCatalogueOptions(studyId, dpId, simulationId, enabled),
  );
}

/**
 * The route answered for a different run than this panel is showing.
 * Reject rather than cache quietly so "Saved" cannot land beside a miss.
 */
function requireCurrentAttempt(
  data: HandCalcGraphResponse | undefined,
  simulationId: string,
  status: number,
  detail: string | null,
  limitReached = false,
): HandCalcGraphResponse {
  if (!data) throw new HandCalcGraphError(status, detail, limitReached);
  if (data.graph.attempt_id !== simulationId)
    throw new HandCalcStaleAttemptError(data.graph.attempt_id);
  return data;
}

/** Write the mutation's answer into the one record entry and refresh offers. */
function cacheHandCalcAnswer(
  client: QueryClient,
  studyId: string,
  dpId: string,
  simulationId: string,
  data: HandCalcGraphResponse,
) {
  client.setQueryData(handCalcRecordKey(studyId, dpId, simulationId), data);
  void client.invalidateQueries({
    queryKey: handCalcCatalogueKey(studyId, dpId, simulationId),
  });
}

/**
 * One edit to the record.
 *
 * The answer IS the new record, so it is written straight into the query
 * entry — there is no second store to keep in step, and a failure leaves the
 * last good response exactly where it was. The catalogue is invalidated
 * rather than patched: the backend rebuilds the offers against the saved
 * record, including any group the edit just created.
 */
export function useHandCalcEdit(
  studyId: string,
  dpId: string,
  simulationId: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationKey: ['hand-calc-edit', studyId, dpId, simulationId],
    // One PATCH at a time per record. Each sends whole lists and the route
    // keeps whichever arrives last, so two in flight could land out of order
    // and save the older lists over the newer. A queued edit's `onMutate`
    // still runs at once, so the overlay below is unchanged; only its request
    // waits for the one ahead of it.
    scope: { id: `hand-calc-edit:${studyId}:${dpId}:${simulationId}` },
    mutationFn: async (body: EditRequest) => {
      const { data, response, error } =
        await handleHandCalcEditStudiesStudyIdPointsDpIdHandCalcPatch({
          path: { study_id: studyId, dp_id: dpId },
          body,
        });
      const answer = requireCurrentAttempt(
        data,
        simulationId,
        response?.status ?? 0,
        apiErrorMessage(error) || null,
        tooLong(error),
      );
      // A preview can run ahead of the shared dev backend. An older PATCH
      // handler may accept an unknown `review` field but return the unchanged
      // record. Do not report that as an approval.
      if (body.review) {
        const target = answer.record?.program.attach?.find(
          (attach) =>
            attach.template_id === body.review?.template_id &&
            attach.scope_id === body.review?.scope_id,
        );
        if (!target || Boolean(target.reviewed_by) !== body.review.reviewed)
          throw new HandCalcGraphError(0);
      }
      return answer;
    },
    // Every list an edit sends is whole, and every builder reads
    // `record.program` from this cache. So while a save is in flight the
    // cache carries the lists it sent: a second edit built meanwhile — a
    // value typed while the last one saves, an attach with typed values —
    // starts from them rather than from the record they replaced, whose
    // wholesale lists would otherwise erase the first edit when they landed.
    // The answer overwrites the overlay; a refusal puts the record back.
    onMutate: (body: EditRequest) => {
      const key = handCalcRecordKey(studyId, dpId, simulationId);
      const previous = client.getQueryData<HandCalcGraphResponse>(key);
      if (previous?.record) {
        const program = { ...previous.record.program };
        if (body.attach) program.attach = body.attach;
        if (body.values) program.values = body.values;
        if (body.reparent) program.reparent = body.reparent;
        client.setQueryData(key, {
          ...previous,
          record: { ...previous.record, program },
        });
      }
      return { previous };
    },
    onError: (_error, _body, context) => {
      // Only when this is the last edit in flight. The snapshot was taken
      // before any edit that started after it, so putting it back while one
      // of those is still running would throw away that edit's answer when
      // it lands; the one still running settles the cache instead.
      const mine = client.isMutating({
        mutationKey: ['hand-calc-edit', studyId, dpId, simulationId],
      });
      if (context?.previous && mine <= 1)
        client.setQueryData(
          handCalcRecordKey(studyId, dpId, simulationId),
          context.previous,
        );
    },
    onSuccess: (data: HandCalcGraphResponse) => {
      cacheHandCalcAnswer(client, studyId, dpId, simulationId, data);
    },
  });
}

/**
 * Redraft the record (ENG-2539). Same cache write as an edit; no optimistic
 * overlay, because a refusal must leave the saved calculation untouched. The
 * attempt is the panel's — a caller-chosen id could redraft a run nobody is
 * looking at. The UI asks before sending: success replaces the engineer's
 * edits too.
 */
export function useHandCalcRegenerate(
  studyId: string,
  dpId: string,
  simulationId: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationKey: ['hand-calc-regenerate', studyId, dpId, simulationId],
    mutationFn: async () => {
      const { data, response, error } =
        await handleHandCalcRegenerateStudiesStudyIdPointsDpIdHandCalcRegeneratePost(
          {
            path: { study_id: studyId, dp_id: dpId },
            body: { attempt_id: simulationId },
          },
        );
      return requireCurrentAttempt(
        data,
        simulationId,
        response?.status ?? 0,
        apiErrorMessage(error) || null,
      );
    },
    onSuccess: (data: HandCalcGraphResponse) => {
      cacheHandCalcAnswer(client, studyId, dpId, simulationId, data);
    },
  });
}
